import { useState, useEffect, useRef } from "react";
import { thumbUrl, videoUrl, posterOf } from "../utils/imageUpload.js";
import { AutoVideo } from "./AutoVideo.jsx";
import { bogoLabel } from "../utils/helpers.js";
import { Icon } from "./Icon.jsx";
import { SoldOutLabel } from "./SoldOutLabel.jsx";
import { RatingBadge } from "./RatingBadge.jsx";
import { advancedHasContent } from "./AdvancedVariations.jsx";

// ─── PRODUCT TILE ────────────────────────────────────────────────────────────
// The product card used across Discover, category pages and "view all" grids (design-system/components.md).
// 4:5 portrait photo (Instagram-seller shots are mostly portrait; swipe through every image, videos included), red discount tag (only when the seller set a higher MRP),
// BOGO offer ribbon, store, two-line name, price, low-stock note, and an add-to-bag button.
// Nothing here is invented: no rating (the marketplace feed has none), no fake stock.
export const productImage = (p) => (Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : (p.img || null));
export const isSoldOut = (p) => !!p.sold || p.is_live === false || Number(p.stock ?? 99) === 0;
export const discountPct = (p) => (Number(p.mrp) > Number(p.price) && Number(p.price) > 0 ? Math.round((1 - Number(p.price) / Number(p.mrp)) * 100) : 0);
// Needs a choice (variation, colour/size, custom question, upload) before it can go in the bag, so its button must NOT add it
// blindly. Advanced variations are stored as an object ({ colour, size, custom }), so they are checked with the same helper the
// storefront and product page use — an array check silently let those products be added with nothing chosen.
export const needsChoice = (p) =>
  (Array.isArray(p.variations) && p.variations.some(v => v && v.name)) || advancedHasContent(p.advanced_variations) ||
  !!p.custom_question || !!p.require_upload;
// Choice is ONLY a simple size/option list (e.g. S / M / L): that can be picked in a small sheet right on the page.
// Anything with colour+size combos, a custom question or a required upload still needs the full product page.
export const quickChoiceOnly = (p) =>
  Array.isArray(p.variations) && p.variations.some(v => v && v.name) && !advancedHasContent(p.advanced_variations) && !p.custom_question && !p.require_upload;
const isVideoUrl = url => /\.(mp4|mov|webm|m3u8)(\?|$)/i.test(url || "");

export function ProductTile({ product, onOpen, onAdd, added = false, fixedWidth, hideStore = false, rating = null, footer = null }) {
  const p = product;
  const images = Array.isArray(p.images) && p.images.length > 0 ? p.images : (p.img ? [p.img] : []);
  const count = images.length;
  const sold = isSoldOut(p);
  const off = discountPct(p);
  const stock = Number(p.stock ?? 99);
  const low = !sold && stock > 0 && stock <= 3;
  const paused = !!(p._storeInfo?.checkout_paused || p.checkout_paused);
  const offer = p.bogo_eligible && bogoLabel(p._storeInfo) && !sold ? bogoLabel(p._storeInfo) : "";
  const [imgIdx, setImgIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [dragPx, setDragPx] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const imgAreaRef = useRef(null);
  const startX = useRef(null);
  const startY = useRef(null);

  // Every image is a real <img> in the DOM from the start (loading="lazy" past index 0 — the browser's own lazy-loading
  // handles fetching them ahead of a swipe), and the slide position is a single transform driven purely by React state
  // (imgIdx + live drag offset), animated with a CSS transition. A transform that's always a pure function of current state
  // can't get stuck half-way (see mktCardImagePreload.test.mjs for the history of that bug).
  const goTo = (i) => { setSnapping(true); setImgIdx(((i % count) + count) % count); setDragPx(0); setTimeout(() => setSnapping(false), 260); };
  const prevImg = (e) => { e.stopPropagation(); goTo(imgIdx - 1); };
  const nextImg = (e) => { e.stopPropagation(); goTo(imgIdx + 1); };

  // Non-passive touchmove is required to preventDefault() the page's own vertical scroll while swiping horizontally —
  // React's JSX touch handlers are passive by default and can't do this.
  useEffect(() => {
    const el = imgAreaRef.current;
    if (!el || count <= 1) return;
    const onStart = (e) => { startX.current = e.touches[0].clientX; startY.current = e.touches[0].clientY; setSnapping(false); };
    const onMove = (e) => {
      if (startX.current === null) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = Math.abs(e.touches[0].clientY - startY.current);
      if (Math.abs(dx) > dy && Math.abs(dx) > 6) {
        e.preventDefault();
        setDragPx(imgIdx === 0 && dx > 0 ? dx * 0.3 : imgIdx === count - 1 && dx < 0 ? dx * 0.3 : dx);
      }
    };
    const onEnd = (e) => {
      if (startX.current === null) return;
      const delta = e.changedTouches[0].clientX - startX.current;
      startX.current = null; startY.current = null;
      if (Math.abs(delta) > 40) { goTo(delta < 0 ? imgIdx + 1 : imgIdx - 1); }
      else { setSnapping(true); setDragPx(0); setTimeout(() => setSnapping(false), 260); }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [count, imgIdx]);

  const arrow = { position: "absolute", top: "50%", transform: "translateY(-50%)", width: 28, height: 28, border: "1px solid var(--border)", borderRadius: 4, background: "var(--white)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, color: "var(--ink)" };
  return (
    <article
      onClick={() => onOpen && onOpen(p)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ width: fixedWidth || "auto", flex: fixedWidth ? "none" : undefined, minWidth: 0, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}>
      <div ref={imgAreaRef} style={{ position: "relative", aspectRatio: "4 / 5", background: "var(--surface)", overflow: "hidden" }}>
        {count === 0 ? (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-soft)" }}><Icon n="image" size={32} /></div>
        ) : (
          <div style={{
            display: "flex", width: `${count * 100}%`, height: "100%",
            transform:`translateX(calc((${-imgIdx * 100}% + ${dragPx}px) / ${count}))`,
            transition: snapping ? "transform 0.25s cubic-bezier(0.25,0.46,0.45,0.94)" : "none",
          }}>
            {images.map((s, i) => (
              <div key={i} style={{ width: `${100 / count}%`, flexShrink: 0, height: "100%" }}>
                {isVideoUrl(s)
                  ? (i === imgIdx
                      ? <AutoVideo src={videoUrl(s)} poster={!isVideoUrl(posterOf(p)) ? thumbUrl(posterOf(p), 900) : undefined} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ width: "100%", height: "100%", background: !isVideoUrl(posterOf(p)) ? `url(${thumbUrl(posterOf(p), 900)}) center/cover` : "var(--surface)" }} />)
                  : <img src={thumbUrl(s, 900)} alt={p.name} loading={i === 0 ? "eager" : "lazy"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
              </div>
            ))}
          </div>
        )}
        {off > 0 && !sold && <span style={{ position: "absolute", left: 0, top: 10, background: "var(--ink)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: "0 2px 2px 0", zIndex: 2 }}>{off}% off</span>}
        {added && !sold && <span style={{ position: "absolute", right: 8, top: 8, background: "var(--teal)", color: "var(--on-accent)", fontSize: 12, fontWeight: 600, padding: "3px 8px", borderRadius: 2, display: "inline-flex", alignItems: "center", gap: 4, zIndex: 3 }}><Icon n="done" size={12} strokeWidth={2.2} />In bag</span>}
        {offer && <span style={{ position: "absolute", right: 8, top: added ? 34 : 8, background: "#FFFAEB", color: "#B54708", border: "1px solid #FEC84B", fontSize: 11, fontWeight: 700, padding: "3px 7px", borderRadius: 2, zIndex: 2 }}>{offer}</span>}
        {/* Dot indicators */}
        {images.length > 1 && !sold && (
          <div style={{ position: "absolute", bottom: 6, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 4, zIndex: 2 }}>
            {images.slice(0, 5).map((_, i) => (
              <div key={i} style={{ width: i === imgIdx ? 14 : 5, height: 5, borderRadius: 2, background: i === imgIdx ? "var(--teal)" : "rgba(20,23,31,0.35)", transition: "all 0.15s" }} />
            ))}
          </div>
        )}
        {/* Desktop hover arrows */}
        {images.length > 1 && hovered && (
          <>
            <button type="button" aria-label="Previous photo" onClick={prevImg} style={{ ...arrow, left: 6, zIndex: 3 }}><Icon n="chevron-left" size={16} /></button>
            <button type="button" aria-label="Next photo" onClick={nextImg} style={{ ...arrow, right: 6, zIndex: 3 }}><Icon n="chevron-right" size={16} /></button>
          </>
        )}
      </div>
      <div style={{ padding: "10px 10px 12px", display: "flex", flexDirection: "column", flex: 1 }}>
        {!hideStore && <div style={{ fontSize: 12, color: "var(--ink-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p._storeInfo?.store_name || ""}</div>}
        <div style={{ fontSize: 14, fontWeight: 500, marginTop: hideStore ? 0 : 2, lineHeight: 1.3, height: footer ? undefined : 36, maxHeight: 36, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.name}</div>
        {rating && rating.count > 0 && <div style={{ marginTop: 4 }}><RatingBadge rating={rating} /></div>}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: footer ? "auto" : 8, paddingTop: footer ? 8 : 0, gap: 6 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>₹{Number(p.price || 0).toLocaleString("en-IN")}</span>
            {Number(p.mrp) > Number(p.price) && <span style={{ fontSize: 12, color: "var(--ink-soft)", textDecoration: "line-through" }}>₹{Number(p.mrp).toLocaleString("en-IN")}</span>}
          </div>
          {sold && !footer && <SoldOutLabel />}
          {onAdd && !footer && !sold && !paused && (
            <button type="button" aria-label={needsChoice(p) ? "Choose options" : "Add to bag"} title={needsChoice(p) ? "Choose options" : "Add to bag"}
              onClick={(e) => { e.stopPropagation(); onAdd(p); }}
              // 44×44 touch target (the visible square is 34px); negative margins keep the row's size unchanged
              style={{ width: 44, height: 44, margin: "-6px -6px -6px 0", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, color: "var(--ink)", touchAction: "manipulation" }}>
              <span style={{ width: 34, height: 34, border: "1px solid #948C80", borderRadius: 4, background: "var(--white)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon n={needsChoice(p) ? "chevron-down" : "bag-add"} size={18} />
              </span>
            </button>
          )}
        </div>
        {footer && <div style={{ marginTop: 10 }}>{footer}</div>}
        {(() => {
          // With a footer (storefront) the sold-out state already has its own "Request restock" button, so don't repeat it as text.
          const note = low ? `Only ${stock} left` : sold && !footer ? "Request a restock" : paused ? "Orders paused" : "";
          return note ? <div style={{ fontSize: 12, fontWeight: 500, color: "var(--warn-fg)", marginTop: 4 }}>{note}</div> : (footer ? null : <div style={{ minHeight: 16, marginTop: 4 }} />);
        })()}
      </div>
    </article>
  );
}
